import { getCurrentJourneyContext } from './common/utils/journeyContextRuntime';
const USE_BFF = import.meta.env.VITE_USE_BFF !== 'false';

// Constants for Online Sales Journey
export const JOURNEY_TYPES = {
  NISCHIT_PENSION: 'nischit-pension',
};

export const JOURNEY_PLAN_NAMES = {
  [JOURNEY_TYPES.NISCHIT_PENSION]: 'Nischit Pension',
};

export const COMPONENT_TYPES = {
  LEAD: 'lead',
  QUOTE: 'quote',
  SUMMARY: 'summary',
  CUSTOMER_VERIFICATION: 'customerVerification',
  // Future component types can be added here
};

// API Configuration
const getBaseURL = () => {
  if (typeof Liferay !== 'undefined' && Liferay.ThemeDisplay) {
    if (USE_BFF) {
      return Liferay.ThemeDisplay.getPortalURL() + '/o/bff-api/proxy';
    }
    return Liferay.ThemeDisplay.getPortalURL();
  }
  return window.location.origin;
};

export const API_BASE_URL = getBaseURL();

export const getPortalBaseURL = () => {
  if (typeof Liferay !== 'undefined' && Liferay.ThemeDisplay) {
    return Liferay.ThemeDisplay.getPortalURL();
  }
  return window.location.origin ;
};

/** Backward compatibility export. Route is resolved from active journey context. */
export const TERM_JOURNEY_PATH = '/retirement-plan';

/** sessionStorage: failed payment → user chose Reinitiate Payment; keep them on /summary after journey hydrate. */
export const REINITIATE_PAYMENT_SUMMARY_SESSION_KEY = 'rnlic_reinitiatePaymentSummary';

/**
 * Full URL for the common journey page.
 * Use this when redirecting after the first continue in lead stage 1 so the journey
 * always runs on the same canonical page regardless of where the lead was dropped.
 */
export const getTermJourneyCanonicalUrl = () => {
  const base = typeof Liferay !== 'undefined' && Liferay.ThemeDisplay
    ? Liferay.ThemeDisplay.getPortalURL()
    : window.location.origin;
  const context = getCurrentJourneyContext();
  const contextPath = typeof context?.routePath === 'string' ? context.routePath.trim() : '';
  const locationPath = typeof window !== 'undefined' && window.location?.pathname
    ? window.location.pathname.trim()
    : '';
  const activePath = contextPath || locationPath;
  return base.replace(/\/$/, '') + activePath;
};

// API endpoints
export const API_ENDPOINTS = {
  OAUTH_TOKEN: '/o/oauth2/token',
  SUBMIT_LEAD: '/o/restService/termJourney/submitLead',
  CUSTOMER_VERIFICATIONS: '/o/c/customerverifications/',
  PROPOSAL_DATA: '/o/restService/termJourney/proposalData',
  GET_JOURNEY: '/o/restService/termJourney/getJourney',
  PAN_CONTINUE: '/o/restService/termJourney/customerVerification/panContinue',
  AADHAAR_OTP_GENERATE: '/o/restService/journey/aadhaarOtpGenerate',
  AADHAAR_OTP_VALIDATE: '/o/restService/journey/aadhaarOtpValidation',
  GET_FORM_DEFINITION: '/o/restService/getFormDefinition',
  JOURNEY_MASTERS: '/o/restService/journeyMasters',
  BECOME_AN_ADVISOR: '/o/restService/becomeAnAdvisor',
  QUOTE_INIT: '/o/restService/quote/quoteInit',
  SUBMIT_QUOTE: '/o/restService/quote/submitQuote',
  UPDATE_QUOTE_SUMMARY: '/o/restService/quote/updateQuoteSummary',
  QUOTE_SUMMARY: '/o/restService/quote/quoteSummary',
  GENERATE_PAYMENT_URL: '/o/restService/payment/initiate',
  PAYMENT_INITIATE: '/o/restService/payment/initiate',
  PAYMENT_STATUS: '/o/restService/payment/status',
  PAYMENT_RETURN_CONTEXT: '/o/restService/payment/returnContext',
  PAYMENT_RECEIPT: '/o/restService/payment/receipt',
  PROPOSAL_FORM: '/o/restService/proposal/form',
  TOF_CONSENT_PDF: '/o/restService/nischit/transferOfFunds/consentPdf',
  CALCULATE_PREMIUM: '/o/restService/premium/calculate',
  CALCULATE_MULTI_PREMIUM: '/o/restService/premium/calculateMulti',
  CALCULATE_BULK_PREMIUM: '/o/restService/premium/calculateBulk',
  CALCULATE_NISHCHIT_BULK: '/o/restService/premium/calculateNishchitBulk',
  PROPOSAL_SUMMARY: '/o/restService/proposalSummary',
  JOURNEY_COMPLETION: '/o/restService/termJourney/journeyCompletion',
  GET_POLICY_STATUS: '/o/restService/journey/getPolicyStatus',
  GENERATE_OTP: '/o/restService/generateOTP',
  VERIFY_OTP: "/o/restService/verifyOTP",
  TERM_JOURNEY_GENERATE_OTP: '/o/restService/termJourney/generateOTP',
  TERM_JOURNEY_VERIFY_OTP: '/o/restService/termJourney/verifyOTP',
  BANK_ACCOUNT_VALIDATE: '/o/restService/journey/bankAccountValidate',
  DOCUMENT_UPLOAD: "/o/restService/journey/documentUpload",
  PIVC_LINK: "/o/restService/journey/createPivcLink",
  PINCODE: '/o/restService/journeyMasters?masterType=getProductPincodes',
  GET_CONTEXT: 'o/inlic/webService/getContent',
};

// Retry constants
export const MAX_RETRY_COUNT = 2;

// HTTP status codes
export const HTTP_STATUS = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
};

// Error messages
export const ERROR_MESSAGES = {
  GENERIC_ERROR: "We couldn't complete this action right now. Please try again in a moment.",
  UNABLE_TO_REFRESH_TOKEN: 'Unable to refresh authentication token.',
  REQUEST_FAILED_AFTER_RETRIES: (retryCount) => `Request failed after ${retryCount} retries.`,
  FIELD_REQUIRED: (fieldName) => `${fieldName} is required.`,
  FORM_LOAD_ERROR: 'Failed to load form structure. Please try again later.',
  FORM_SUBMIT_ERROR: 'Failed to submit form. Please try again.',
  TOF_POLICY_NOT_FOUND:
    "We couldn't find any records for the policy number entered. Please check the policy number and try again.",
  TOF_POLICY_LOOKUP_FAILED:
    "We're unable to retrieve your policy details at the moment. Please try again later.",
};

// Success messages
export const SUCCESS_MESSAGES = {
  FORM_SUBMITTED: 'Your form has been submitted successfully.',
};

// Default premium calculation inputs (fallbacks until BI logic is wired)
export const DEFAULT_BASE_SUM_ASSURED = 6000000;
export const DEFAULT_POLICY_TERM = 25;
export const DEFAULT_PREMIUM_PAYING_TERM = 7;

// Premium payment modes for multi-dimension calculation
// Values must match Excel dropdown/validation values exactly
export const PREMIUM_PAYMENT_MODES = ['Yearly', 'Half-yearly', 'Quarterly', 'Monthly'];

// Plus plan: numeric frequency codes for premiumPaymentModeCode
// Maps string payment mode → number expected by Plus Excel (C31)
export const PREMIUM_PAYMENT_MODE_CODES = {
  'Yearly': 1,
  'Half-yearly': 2,
  'Quarterly': 4,
  'Monthly': 12,
};

// Limited Pay (LP) term options
// Plus: 5, 7, 10, 15, 20. Elite: 5, 7, 10, 15.
export const DEFAULT_LIMITED_PAY_OPTIONS = [5, 7, 10, 15];

/**
 * LP term options to request from API, aligned with Quote UI.
 * Plus: 5, 7, 10, 15, 20. Elite: 5, 7, 10, 15.
 * Applies business rule: PT - PPT >= 9 (and therefore PT >= PPT).
 * @param {string} plan - PLAN_NAMES.PLUS or PLAN_NAMES.ELITE
 * @param {number} [policyTerm] - policy term in years (default 50)
 * @returns {number[]}
 */
export const getLimitedPayOptionsForPlan = (plan, policyTerm = 50) => {
  const pt = Number(policyTerm);
  const maxAllowedPptByPt = Number.isFinite(pt) ? pt - 9 : Number.POSITIVE_INFINITY;
  const baseOptions =
    plan === PLAN_NAMES_NISCHIT_PENSION.PLAN_A ? [5, 7, 10, 15, 20] : [5, 7, 10, 15];
  return baseOptions.filter((ppt) => ppt <= maxAllowedPptByPt);
};

// Payment term type constants
export const PAYMENT_TERM_TYPE = {
  LIMITED_PAY: 'LP',
  REGULAR_PAY: 'RP',
};

// Super Suraksha plan display names and BI product codes (for multi-dimension and quote)
export const PRODUCT_CODES_NISCHIT_PENSION = {
  PLAN_A: '22074',
  PLAN_B: '22070',
};
export const PLAN_NAMES_NISCHIT_PENSION = {
  PLAN_A: 'Single Life Annuity',
  PLAN_B: 'Joint Life Annuity',
};
export const PLAN_PRODUCT_CODES_NISCHIT_PENSION = {
  [PLAN_NAMES_NISCHIT_PENSION.PLAN_A]: PRODUCT_CODES_NISCHIT_PENSION.PLAN_A,
  [PLAN_NAMES_NISCHIT_PENSION.PLAN_B]: PRODUCT_CODES_NISCHIT_PENSION.PLAN_B,
};


/** Life cover sum assured step (rupees) — grid options and workbook align to this increment. */
export const COVER_AMOUNT_RUPEE_STEP = 100_000;

/** Debounce before calling multi-premium API while user types custom cover (ms). */
export const CUSTOM_COVER_PREMIUM_DEBOUNCE_MS = 400;

/** Debounce before quote grid prefetch (cover tiers + cover-till-age) to avoid API bursts (ms). */
export const GRID_PREFETCH_DEBOUNCE_MS = 450;

export const MIN_COVER_ELITE = 20_000_000;
/** Minimum cover (sum assured) for Plus product – least option shown is 50 Lakh. */
export const MIN_COVER_PLUS = 5_000_000;

/**
 * Plus product: maximum attained age at end of cover (aligns with
 * ProductEligibilityUtil.MAX_PLUS_COVER_TILL_AGE).
 */
export const MAX_COVER_TILL_AGE_PLUS = 75;

/** Minimum policy term in years (eligibility / term journey). */
export const MIN_POLICY_TERM_YEARS = 20;

/**
 * Default cover amount (max option) for initial premium fetch.
 * Matches the highest value from generateCoverAmountOptions in Quote.
 */
export const getDefaultCoverAmount = (maxCoverAmount) => {
  const max = Number(maxCoverAmount);
  const step = COVER_AMOUNT_RUPEE_STEP;
  if (!max || isNaN(max)) return Math.round(50000000 / step) * step;
  return Math.round(max / step) * step;
};

// =====================================================
// Income Range Mapping for BI API
// =====================================================
// BI API expects these codes for @ANNUAL_INCOME field
export const INCOME_RANGE_BI_CODES = {
  UPTO_3_LACS: '4',
  '3_TO_6_LACS': '5',
  '6_TO_10_LACS': '6',
  '10_TO_20_LACS': '7',
  '20_TO_50_LACS': '8',
  ABOVE_50_LACS: '9',
};

// Income ranges in rupees with corresponding BI codes
// Used to determine which range an exact income value falls into
export const INCOME_RANGES = [
  { min: 0, max: 300000, biCode: INCOME_RANGE_BI_CODES.UPTO_3_LACS, label: 'Up To 3 Lacs' },
  { min: 300001, max: 600000, biCode: INCOME_RANGE_BI_CODES['3_TO_6_LACS'], label: '3 Lacs To 6 Lacs' },
  { min: 600001, max: 1000000, biCode: INCOME_RANGE_BI_CODES['6_TO_10_LACS'], label: '6 Lacs To 10 Lacs' },
  { min: 1000001, max: 2000000, biCode: INCOME_RANGE_BI_CODES['10_TO_20_LACS'], label: '10 Lacs To 20 Lacs' },
  { min: 2000001, max: 5000000, biCode: INCOME_RANGE_BI_CODES['20_TO_50_LACS'], label: '20 Lacs To 50 Lacs' },
  { min: 5000001, max: Infinity, biCode: INCOME_RANGE_BI_CODES.ABOVE_50_LACS, label: 'Above 50 Lacs' },
];

/**
 * Get BI code and range label from exact annual income value
 * @param {number|string} income - Annual income in rupees (exact value entered by user)
 * @returns {{ biCode: string, rangeLabel: string }} BI code for API and human-readable range label
 */
export const getIncomeRangeFromValue = (income) => {
  // Parse numeric value from string (remove currency symbols, commas, spaces)
  const numericIncome = typeof income === 'string'
    ? parseFloat(income.replace(/[^0-9.]/g, ''))
    : income;

  // Default to '6' (6 Lacs To 10 Lacs) if invalid - matches backend default
  if (isNaN(numericIncome) || numericIncome <= 0) {
    return { biCode: INCOME_RANGE_BI_CODES['6_TO_10_LACS'], rangeLabel: '6 Lacs To 10 Lacs' };
  }

  // Find the matching range
  const range = INCOME_RANGES.find(r => numericIncome >= r.min && numericIncome <= r.max);

  return range
    ? { biCode: range.biCode, rangeLabel: range.label }
    : { biCode: INCOME_RANGE_BI_CODES.ABOVE_50_LACS, rangeLabel: 'Above 50 Lacs' };
};

// Form names by journey type and stage.
// Each custom element can override these at runtime via element attributes.
const JOURNEY_FORM_NAMES = {
  [JOURNEY_TYPES.NISCHIT_PENSION]: {
    lead: 'Nischit Pension Term Lead Form',
  },
};
 

/**
 * Get the form name for a given journey stage
 * @param {string} stage - Journey stage (lead, quote, etc.)
 * @returns {string} Form name configured for the stage
 */
export const getFormName = (stage = 'lead') => {
  const normalizedStage = String(stage || 'lead').toLowerCase();
  const context = getCurrentJourneyContext();
  const activeJourneyType = context?.journeyType || JOURNEY_TYPES.NISCHIT_PENSION;

  const contextOverride = context?.formNames?.[normalizedStage];
  if (contextOverride) return contextOverride;

  const typedForms =
    JOURNEY_FORM_NAMES[activeJourneyType] ||
    JOURNEY_FORM_NAMES[JOURNEY_TYPES.NISCHIT_PENSION];

  return (
    typedForms?.[normalizedStage] ||
    JOURNEY_FORM_NAMES[JOURNEY_TYPES.NISCHIT_PENSION].lead
  );
};

export const getJourneyPlanName = (journeyType) => {
  const context = getCurrentJourneyContext();
  const activeJourneyType =
    journeyType ||
    context?.journeyType ||
    JOURNEY_TYPES.NISCHIT_PENSION;
  return (
    JOURNEY_PLAN_NAMES[activeJourneyType] ||
    JOURNEY_PLAN_NAMES[JOURNEY_TYPES.NISCHIT_PENSION]
  );
};

/**
 * Get the site group ID from Liferay ThemeDisplay
 * @returns {number|null} Site group ID or null if not available
 */
export const getSiteGroupId = () => {
  if (typeof Liferay !== 'undefined' && Liferay.ThemeDisplay) {
    return Liferay.ThemeDisplay.getSiteGroupId();
  }
  return null;
};

export const DOCUMENT_UPLOAD_TEXT = "Select the documents you will upload as proof for verification.";

export const DECLARATIONS = {
  declaration: [
    { label: "d1", value: "I/We hereby declare, on my/our behalf and, that the above statements, answers and/or particulars given by my/our are true and complete in all respects to the best of my/our knowledge. I/We understand and agree that the statements in this proposal form shall be the basis of the contract between me/us and IndusInd Nippon Life Insurance Company Limited." },
  ],
  authorization: [
    { label: "a1", value: "I/We further provide my/our consent to the Company to share my/our details to regulated entities and with any other institution as authorized by the IRDAI including but not limited to Insurance Information Bureau, Insurance Repositories, CERSAI. Payments will be made to the provided bank a/c, unless the bank a/c particulars are changed/modified by my/our written communication to INLIC." },
    { label: "a2", value: "In accordance with regulatory requirement, we will send soft copy of policy document to your registered email ID. In addition to this, You have an option to receive the physical copy, if required, by sending a request from your registeredemail ID to customerservice@indusindnipponlife.com or call us at 1800 102 1010 (toll free no) from your registered mobile number." },
    { label: "a3", value: "I understand that my KYC Record includes my KYC Records /Personal information such as my name, address, date of birth, PAN number etc. I further undertake to inform the Company immediately upon any changes in the KYC documents." },
  ],
};

export const consentDeclarationsContent = {
  heading: { label: "heading", value: "Consent and Declarations" },
  paragraphs: [
    { label: "p1", value: "Please review and confirm the following declarations carefully. By providing your consent, you acknowledge that all the information shared by you is true, complete, and accurate to the best of your knowledge." }, 
    { label: "p2", value: "This consent allows us to process your request, verify details, and issue or service your Policy as per applicable terms and regulatory guidelines." },
    { label: "p3", value: "Your information will be used solely for policy-related purpose and handled securely in accordance with our privacy and data protection practices." },

  ]
};

export const ANNUITANT_FIELDS = [
  { fieldReference: "saTitle", label: "Title", required: true },
  { fieldReference: "saFullName", label: "Full Name", required: true },
  { fieldReference: "saEmail", label: "Email", required: true },
  { fieldReference: "saFatherName", label: "Father Name", required: true },
  { fieldReference: "saMotherName", label: "Mother Name", required: true },
  {
    fieldReference: "saMaritalStatus",
    label: "Marital Status",
    required: true,
  },
  { fieldReference: "saPanNumber", label: "PAN NO.", required: true },
  { fieldReference: "saEducation", label: "Education", required: true },
  { fieldReference: "saAnnualIncome", label: "Annual Income", required: true },
  { fieldReference: "saJobDescription", label: "Job Description", required: true },
];
export const  SA_RADIO_FIELD = [
  { fieldReference: "saCommunicationAddressSameAsProposerAddress", label: "Use the same address as proposer", required: true },
  { fieldReference: "sameSaBankAsProposer", label: "Use the same bank details as the Proposer", required: true },
];
export const  PA_RADIO_FIELD = [
  { fieldReference: "paCommunicationAddressSameAsProposerAddress", label: "Use the same address as proposer", required: true },
  { fieldReference: "samePaBankAsProposer", label: "Use the same bank details as the Proposer", required: true },
];

export const PERSONAL_FIELDS = [
  { fieldReference: "paTitle", label: "Title", required: true },
  { fieldReference: "paFullName", label: "Full Name", required: true },
  { fieldReference: "paEmail", label: "Email", required: true },
  { fieldReference: "paFatherName", label: "Father Name", required: true },
  { fieldReference: "paMotherName", label: "Mother Name", required: true },
  {
    fieldReference: "paMaritalStatus",
    label: "Marital Status",
    required: true,
  },
  { fieldReference: "paPanNumber", label: "PAN NO.", required: true },
  { fieldReference: "paEducation", label: "Education", required: true },
  { fieldReference: "paAnnualIncome", label: "Annual Income", required: true },
  { fieldReference: "paJobDescription", label: "Job Description", required: true },
  // { fieldReference: "paCommunicationAddressSameAsProposerAddress", label: "Use the same address as proposer", required: true },
  // { fieldReference: "samePaBankAsProposer", label: "Use the same bank details as the Proposer", required: true },
  
];

export const ADDITIONAL_FIELDS = [
  { fieldReference: "paPhotograph", label: "Photograph", required: true },
  { fieldReference: "paAddressProof", label: "Address Proof", required: true },
  { fieldReference: "paAgeProof", label: "Age Proof", required: true },
  { fieldReference: "paIncomeProof", label: "Financial/Income Documents", required: true },
  {
    fieldReference: "paIdentificationProof",
    label: "Medical Documents",
    required: true,
  },
  {
    fieldReference: "paIdentificationNumber",
    label: "Identification Number",
    required: true,
  },
  {
    fieldReference: "paBankAccountProof",
    label: "Bank Account Proof",
    required: true,
  },
];
export const ANNUITANT_ADDITIONAL_FIELDS = [
  { fieldReference: "saPhotograph", label: "Photograph", required: true },
  { fieldReference: "saAddressProof", label: "Address Proof", required: true },
  { fieldReference: "saAgeProof", label: "Age Proof", required: true },
  { fieldReference: "saIncomeProof", label: "Financial/Income Documents", required: true },
  {
    fieldReference: "saIdentificationProof",
    label: "Medical Documents",
    required: true,
  },
  {
    fieldReference: "saIdentificationNumber",
    label: "Identification Number",
    required: true,
  },
  {
    fieldReference: "saBankAccountProof",
    label: "Bank Account Proof",
    required: true,
  },
];

export const SIDE_STEPSS = [
  {
    id: "lifestyle",
    label: "Lifestyle Details",
    description: "We need this info to know about your healthy lifestyle.",
  },
  {
    id: "medical",
    label: "Medical Details",
    description:
      "Precise information is essential for smooth processing of medical policies.",
  },
];

export const SALUTATION_GENDER_MAP = {
  Mr: "Male",
  Sir: "Male",
  Master: "Male",

  Ms: "Female",
  Miss: "Female",
  Mrs: "Female",
  Madam: "Female",

  Doctor: "neutral",
  Professor: "neutral",
  Reverend: "neutral",
  "Rt Hon": "neutral",
};

export const PHOTOGRAPH_OPTIONS = [
  { label: "Passport Photo", value: "Passport Photo" },
  { label: "Selfie", value: "Selfie" },
];

export const INCOME_PROOF_OPTIONS = ["Recent Appointment Letter/Salary revision Letter", "Last 3 month's salary slips", "Last 6 months bank statements reflecting salary credit at regular periodic interval", "Income Tax Returns (ITR) for last 2 years along with computation of income", "Lates Form 16 / Form 16A", "Form 26As"];

export const EDUCATION_OPTIONS = [
  { label: "Below 10th Standard", value: "Below 10th Standard" },
  { label: "SSLC (10th Standard)", value: "SSLC (10th Standard)" },
  { label: "Plus Two 10+2", value: "Plus Two 10+2" },
  { label: "Diploma after 10th", value: "Diploma after 10th" },
  { label: "Graduate", value: "Graduate" },
  { label: "Post Graduate", value: "Post Graduate" },
];

export const MARITAL_STATUS_OPTIONS = [
  { label: "Single", value: "Single" },
  { label: "Married", value: "Married" },
  { label: "Divorced", value: "Divorced" },
  { label: "Widowed", value: "Widowed" },
];

export const ANNUITANT_STEPS = ["primaryAnnuitant", "secondaryAnnuitant"];
export const STEP_COPY_BY_REFERENCE = {
  personal: {
    title: "How should we address you?",
    subtitle: "How should we address you?",
    bubble: "This helps us personalise your premium and cover options.",
  },
  lifeassured:{
    title: "Who are you buying for?",
  },
  annuitant: {
    title: "Who are you buying for?",
    bubble:
      "Know the annuitant's role in pensions. They get retirement payments.",
  },
  residency: {
    title: "Where do you live?",
    bubble:
      "Location details are required to verify service coverage and risk zone classification.",
  },
  additional: {
    title: "Final Check!",
  },
};

export const proposerHeading = "Complete your personal details in just a few steps!";
export const proposerParagraphText = "Providing accurate details helps us tailor your policy to your specific needs and ensures a smooth setup."

export const annuitantHeading = "Add annuitant details in just a few steps";
export const annuitantParagraphText = "Providing accurate annuitant information helps us set up your annuity correctly and ensures timely and seamless payouts.";

export const nomineeHeading = "To honour your claims, we need nominee details. Our team is here to help!"
export const nomineeParagraphText = " Precise information helps customize your policy, making the process easier."

export const medicalHeading ="Share your health details with us it's easy, and we’re here to support you!"
export const medicalParagraphText ="Precise information helps customize your policy, making the process easier."

export const TEXT_CONTENT={

 PERSONALIZE_TEXT:"Let’s personalise your plan!",

 LOCATION:"Where do you live?",

 CHECK:"Final Check!",
TOGGLE_TWO: "Whether the Life to be Insured/Proposer/Nominee(s)/Appointee(s) is/are Politically Exposed Person(s)?",

FALL_BACK_MSG : "As per the Product Guidelines, you not eligible to take the current term plan however can apply for a pension plan or other traditional or savings plans.",

CUSTOMIZE_PN:"You can customise your plan by changing below parameters",

CALC_FAILURE_MSG:"Premium calculation failed for one or more plan options. Please review your selected values and try again.",

BI_GEN_FAILED:"BI generation failed. Please try again.",

PLAN_CALC_FAILURE:"Plan option calculation failed. Please try again.",

QUOTE_SUBMIT:"Your quote has been submitted successfully. Our team will contact you shortly.",

DECLARATIONS_CHECKBOX:"I declare that I have understood the Benefit Illustration of the proposed plan before entering into the contract.",

DECLARATIONS_CHECKBOXX:"I understand and agree to follow the Terms of Use.",

DECLARATIONS_CHECKBOXXX:"I further understand that the information i submit will be used as described in the Privacy Policy.",

RENEWAL_DESCRIPTION:"Your plan renews automatically—no missed payments, no policy lapses. Stay insured every single day.",

VERIFICATION_TITLE:"Make upcoming forms quicker with smart pre-fill",

VERIFICATION_SUBTITLE:"Share your Aadhaar details to auto-fill proposer information in the next steps.",

AADHAR_VERIFI:"Aadhaar verification is done securely using UIDAI-approved services. Your details are masked and never stored.",

BENEFIT_LIST:"Automatically fills proposer details in the upcoming form",

BENEFIT_LISTT:"Reduces manual typing and errors",

BENEFIT_LISTTT:"Makes the proposal process quicker and smoother",

BENEFIT_FOOTER:"You can skip this step and enter details manually if you prefer.",

PERSONAL_DESCRIPTION:"Accurate details ensure seamless policy issuance.",

BANK_DESCRIPTIONN:"We need this to verify your account.",

SIDE_HEADING:"Complete your personal details in just a few steps!",

SIDE_SUBHEADING:"Providing accurate details helps us tailor your policy to your specific needs and ensures a smooth setup.",

COM_ADDRESS:"Communication address same as permanent address",

RES_ADDRESS:"Resident of Life Assured for Tax purposes in Jurisdiction(s) outside India",

NOM_HEADING:"To honour your claims, we need nominee details. Our team is here to help!",

NOM_SUBHEADING:"Precise information helps customize your policy, making the process easier.",

NOM_ADDRESS:"Nominee Address same as Proposer",

ANN_HEADING:"Add annuitant details in just a few steps",

ANN_SUBHEADING:"Providing accurate annuitant information helps us set up your annuity correctly and ensures timely and seamless payouts.",

PRI_ANN_DETAILS:"Primary annuitant information helps ensure continuity of annuity benefits, where applicable.",

SEC_ANN_DETAILS:"Secondary annuitant information helps ensure continuity of annuity benefits, where applicable.",

ANN_RES_ADD:"Use the same address as proposer",

BANK_RES_ADD:"Use the same bank details as the Proposer",

PRO_SIDE_HEADING:"Please review your details carefully",

PRO_SIDE_SUBHEAD:"The information you provide will be used to issue your policy. Any incorrect or missing details may lead to delays, policy discrepancies, or claim-related issues later.",

PRO_SIDE_SUBHEADD:"Take a moment to verify everything so your coverage stays smooth and hassle-free.",

UPLOAD_DOC:"Upload your identity and Address proofs here for quicker verification purpose.",

UPLOAD_NOTE:"Note: Please upload all mandatory documents before starting Pre-Issuance Verification.",

PIVC_ERROR:"Please upload all mandatory documents before proceeding with Pre-Issuance Verification or OTP",

SUCCESS_BANNER:"Your proposal has been successfully submitted",

SUCCESS_SUBBANNER:"We've received your payment, application details, and documents. Your policy is now under review.",

PAYMENT_COMPLETED:"Your payment has been successfully processed.",

APPLICATION_SUBMITTED:"Your details have been submitted for verification.",

DOCUMENT_UPLOAD:"All required documents have been received.",

PIVC_HEADING:"This is a mandatory verification call conducted before your policy is issued. Our representative will confirm key detailsrelated to your proposal to ensure accuracy and compliance.",

PIVC_SUBHEADING:"Please ensure you are available to take this call and provide accurate information.",

BI_GENERATED:"Your benefit illustration based on the details has been created.",

PAYMENT_DONE:"Your payment has been successfully processed.",

PRO_FORM:"Proposal form has been completed.",

DOC_UPLOAD:"All required documents have been received.",

PIVC_CALL:"A verification call has been conducted to confirm proposal details.",

APP_SUBMITTED:"Your details have been submitted and are currently being reviewed by our Business Ops team.",

REQUIREMENT_RAISED:"Your details will be reviewed by our Underwriting team.",

FINAL_STATUS:"Your contract proposal has been finalized and is ready for the next step.",

HEADING_SUBTEXT:"Stay informed at every step of your policy journey. Check the current status of your application and know exactly what's happening behind the scenes.",

TRACK_BOTTOM:"Your application goes through multiple verification stages to ensure accuracy and compliance. Some steps may take time depending on document review or regulatory checks.If any action is needed from your side,we'll notify you promptly so your application can move forward without delays. You can revisit this page anytime to stay updated and complete pending steps,if any."

}
export const SIDE_STEPS_CONFIG = {
   "nischit-pension": [
    {
      id: "personal",
      label: "Personal Details",
      description: "Accurate details ensure seamless policy issuance.",
    },
    {
      id: "bank",
      label: "Bank Details",
      description: "We need this to verify your account.",
    },
  ],
};

export const RESIDENTIAL_STATUS_OPTIONS = [
  "Resident Individual",
  "Non-Resident Indian",
  "Person of Indian Origin",
];
export const EXISTING_POLICY_STATUS_OPTIONS = ["Inforce", "Lapsed", "Applied"];